import { inject, Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    catchError,
    concatMap,
    exhaustMap,
    map,
    of,
    switchMap,
    tap,
    withLatestFrom,
} from "rxjs";
import {
    AuthenticationApiError,
    AuthenticationApiService,
} from "../data-access/authentication-api.service";
import { QrLoginCallbackService } from "../data-access/qr-login-callback.service";
import { CredentialStore } from "../data-access/credential.store";
import { landingUrlFor } from "../services/landing-policy";
import { SessionActions } from "./session.actions";
import { sessionFeature } from "./session.reducer";
import { TranslateService } from "@ngx-translate/core";
import { AuthenticatedImageService } from "../../../core/media/authenticated-image.service";
import { PostLoginRedirectService } from "../services/post-login-redirect.service";
import { CompanyBillingApiService } from "../../billing/data-access/company-billing-api.service";
import { resolveCompanyEntitlement } from "../../billing/services/company-entitlement.resolver";
import { CompanyEntitlementActions } from "../../billing/store/company-entitlement.actions";

@Injectable()
export class SessionEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly gateway = inject(AuthenticationApiService);
    private readonly companyBilling = inject(CompanyBillingApiService);
    private readonly credentials = inject(CredentialStore);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly authenticatedImages = inject(AuthenticatedImageService);
    private readonly postLoginRedirect = inject(PostLoginRedirectService);
    private readonly qrCallback = inject(QrLoginCallbackService);
    private readonly requestedUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    // A QR callback supplies a device code; restoration must keep the current
    // browser identity while the callback owns navigation back into the shell.
    private readonly requestedQrCallback =
        this.requestedUrl.startsWith("/azure/short_value/") ||
        ((this.requestedUrl.startsWith("/azure/value/") ||
            this.requestedUrl.split(/[?#]/)[0] === "/azuread_accept_token") &&
            this.qrCallback.hasAzureIntent());

    readonly restore$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.applicationStarted),
            switchMap(() => {
                // The callback supplies a new identity; do not race it with
                // restoration (and navigation) from a previously saved account.
                if (
                    this.requestedUrl.split(/[?#]/)[0] ===
                        "/azuread_accept_token" &&
                    !this.requestedQrCallback
                )
                    return of(SessionActions.noStoredCredentials());
                const tokens = this.credentials.readTokens();
                if (!tokens) return of(SessionActions.noStoredCredentials());
                return of(
                    SessionActions.restoreStarted(),
                    SessionActions.credentialsAccepted({
                        navigation: "preserve",
                    })
                );
            })
        )
    );

    readonly resolveAccounts$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.passwordLoginRequested),
            exhaustMap(({ email }) => {
                const pending = this.credentials.getPendingLogin();
                if (!pending)
                    return of(
                        SessionActions.authenticationFailed({
                            message: this.translate.instant(
                                "auth.errors.credentialsUnavailable"
                            ),
                        })
                    );
                return this.gateway
                    .resolveAccounts(email, pending.password)
                    .pipe(
                        concatMap((accounts) => {
                            if (accounts.length === 0)
                                return of(
                                    SessionActions.authenticationFailed({
                                        message: this.translate.instant(
                                            "auth.errors.invalidCredentials"
                                        ),
                                    })
                                );
                            const resolved = SessionActions.accountsResolved({
                                accounts,
                            });
                            return accounts.length === 1
                                ? of(
                                      resolved,
                                      SessionActions.accountSelected({
                                          username: accounts[0].username,
                                      })
                                  )
                                : of(resolved);
                        }),
                        catchError((error: unknown) =>
                            of(
                                SessionActions.authenticationFailed({
                                    message:
                                        error instanceof Error
                                            ? error.message
                                            : this.translate.instant(
                                                  "auth.errors.loginFailed"
                                              ),
                                })
                            )
                        )
                    );
            })
        )
    );

    readonly startAzureLogin$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SessionActions.azureLoginRequested),
                tap(() => {
                    this.postLoginRedirect.rememberFromLoginUrl(
                        this.router.url
                    );
                    window.location.assign(this.gateway.getAzureLoginUrl());
                })
            ),
        { dispatch: false }
    );

    readonly exchangeAzureToken$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.azureCallbackReceived),
            exhaustMap(({ shortToken }) =>
                this.gateway.exchangeAzureShortToken(shortToken).pipe(
                    tap((tokens) => this.credentials.saveTokens(tokens)),
                    map(() =>
                        SessionActions.credentialsAccepted({
                            navigation: "landing",
                        })
                    ),
                    catchError(() =>
                        of(
                            SessionActions.authenticationFailed({
                                message: this.translate.instant(
                                    "auth.azure.errors.exchangeFailed"
                                ),
                            })
                        )
                    )
                )
            )
        )
    );

    readonly exchangeCredentials$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.accountSelected),
            exhaustMap(({ username }) => {
                const pending = this.credentials.getPendingLogin();
                if (!pending)
                    return of(
                        SessionActions.authenticationFailed({
                            message: this.translate.instant(
                                "auth.errors.credentialsUnavailable"
                            ),
                        })
                    );
                return this.gateway
                    .login(username, pending.email, pending.password)
                    .pipe(
                        map((result) => {
                            if (
                                result.kind === "challenge" ||
                                result.kind === "enrollment"
                            ) {
                                this.credentials.setPendingTwoFactor(
                                    result.username,
                                    result.loginSecret!
                                );
                                return result.kind === "challenge"
                                    ? SessionActions.twoFactorChallengeRequired()
                                    : SessionActions.twoFactorEnrollmentRequired();
                            }
                            this.credentials.saveTokens(result.tokens!);
                            return SessionActions.credentialsAccepted({
                                navigation: "landing",
                            });
                        }),
                        catchError((error: unknown) =>
                            of(
                                SessionActions.authenticationFailed({
                                    message:
                                        error instanceof Error
                                            ? error.message
                                            : this.translate.instant(
                                                  "auth.errors.loginFailed"
                                              ),
                                })
                            )
                        )
                    );
            })
        )
    );

    readonly verifyTwoFactor$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.twoFactorCodeSubmitted),
            withLatestFrom(this.store.select(sessionFeature.selectStatus)),
            exhaustMap(([{ secret }, status]) => {
                const challenge = this.credentials.getPendingTwoFactor();
                if (!challenge) return of(SessionActions.twoFactorCancelled());
                return this.gateway
                    .verifyTwoFactor(
                        challenge.username,
                        challenge.loginSecret,
                        secret
                    )
                    .pipe(
                        map(({ tokens, recoveryCodes }) => {
                            if (
                                status === "two-factor-enrollment-code" ||
                                recoveryCodes.length
                            ) {
                                this.credentials.setPendingTokens(tokens);
                                if (recoveryCodes.length)
                                    this.credentials.setRecoveryCodes(
                                        recoveryCodes
                                    );
                                return SessionActions.twoFactorRecoveryCodesIssued();
                            }
                            this.credentials.saveTokens(tokens);
                            this.credentials.clearTwoFactorFlow();
                            return SessionActions.credentialsAccepted({
                                navigation: "landing",
                            });
                        }),
                        catchError((error: unknown) =>
                            of(
                                SessionActions.twoFactorFailed({
                                    message: this.errorMessage(error),
                                })
                            )
                        )
                    );
            })
        )
    );

    readonly verifyRecoveryCodes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.twoFactorRecoverySubmitted),
            exhaustMap(({ codes }) => {
                const challenge = this.credentials.getPendingTwoFactor();
                if (!challenge) return of(SessionActions.twoFactorCancelled());
                return this.gateway
                    .verifyRecoveryCodes(
                        challenge.username,
                        challenge.loginSecret,
                        codes
                    )
                    .pipe(
                        map(({ tokens, recoveryCodes }) => {
                            if (recoveryCodes.length) {
                                this.credentials.setPendingTokens(tokens);
                                this.credentials.setRecoveryCodes(
                                    recoveryCodes
                                );
                                return SessionActions.twoFactorRecoveryCodesIssued();
                            }
                            this.credentials.saveTokens(tokens);
                            this.credentials.clearTwoFactorFlow();
                            return SessionActions.credentialsAccepted({
                                navigation: "landing",
                            });
                        }),
                        catchError((error: unknown) =>
                            of(
                                SessionActions.twoFactorFailed({
                                    message: this.errorMessage(error),
                                })
                            )
                        )
                    );
            })
        )
    );

    readonly beginEnrollment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.twoFactorEnrollmentPhoneSubmitted),
            exhaustMap(({ phone }) => {
                const challenge = this.credentials.getPendingTwoFactor();
                if (!challenge) return of(SessionActions.twoFactorCancelled());
                return this.gateway
                    .beginEnforcedTwoFactor(
                        challenge.username,
                        challenge.loginSecret,
                        phone
                    )
                    .pipe(
                        tap((codes) =>
                            this.credentials.setRecoveryCodes(codes)
                        ),
                        map(() =>
                            SessionActions.twoFactorEnrollmentCodeRequired()
                        ),
                        catchError((error: unknown) =>
                            of(
                                SessionActions.twoFactorFailed({
                                    message: this.errorMessage(error),
                                })
                            )
                        )
                    );
            })
        )
    );

    readonly acknowledgeRecoveryCodes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.twoFactorRecoveryCodesAcknowledged),
            map(() => {
                const tokens = this.credentials.takePendingTokens();
                if (!tokens) return SessionActions.twoFactorCancelled();
                this.credentials.saveTokens(tokens);
                this.credentials.clearTwoFactorFlow();
                return SessionActions.credentialsAccepted({
                    navigation: "landing",
                });
            })
        )
    );

    readonly cancelTwoFactor$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    SessionActions.twoFactorCancelled,
                    SessionActions.loginReset
                ),
                tap(() => this.credentials.clearTwoFactorFlow())
            ),
        { dispatch: false }
    );

    readonly loadCurrentUser$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SessionActions.credentialsAccepted,
                SessionActions.workspaceLoadRetryRequested
            ),
            switchMap((action) => {
                const navigation =
                    "navigation" in action ? action.navigation : "landing";
                const tokens = this.credentials.readTokens();
                if (!tokens)
                    return of(
                        SessionActions.authenticationFailed({
                            message: this.translate.instant(
                                "auth.errors.sessionCredentialsUnavailable"
                            ),
                        })
                    );
                return this.gateway.whoAmI(tokens).pipe(
                    tap((result) => this.credentials.saveTokens(result.tokens)),
                    switchMap(({ user, tokens: refreshedTokens }) => {
                        if (user.role !== "user") {
                            this.store.dispatch(
                                CompanyEntitlementActions.cleared()
                            );
                            return of(
                                SessionActions.currentUserLoaded({
                                    user,
                                    navigation,
                                })
                            );
                        }

                        this.store.dispatch(
                            CompanyEntitlementActions.bootstrapLoadStarted({
                                clientId: user.clientId,
                            })
                        );
                        return this.companyBilling.load(user.clientId).pipe(
                            map((snapshot) => {
                                this.store.dispatch(
                                    CompanyEntitlementActions.loadSucceeded({
                                        entitlement: resolveCompanyEntitlement(
                                            snapshot,
                                            refreshedTokens.source
                                        ),
                                        company: snapshot.company,
                                    })
                                );
                                return SessionActions.currentUserLoaded({
                                    user,
                                    navigation,
                                });
                            }),
                            catchError((error: unknown) => {
                                const message =
                                    this.criticalLoadErrorMessage(error);
                                this.store.dispatch(
                                    CompanyEntitlementActions.loadFailed({
                                        message,
                                    })
                                );
                                return of(
                                    SessionActions.workspaceLoadFailed({
                                        message,
                                    })
                                );
                            })
                        );
                    }),
                    catchError((error: unknown) => {
                        if (this.isInvalidSession(error)) {
                            this.credentials.clearAll();
                            return of(
                                SessionActions.authenticationFailed({
                                    message: this.translate.instant(
                                        "auth.errors.sessionExpired"
                                    ),
                                })
                            );
                        }
                        return of(
                            SessionActions.workspaceLoadFailed({
                                message: this.criticalLoadErrorMessage(error),
                            })
                        );
                    })
                );
            })
        )
    );

    readonly loginCompleted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SessionActions.currentUserLoaded),
                tap(() => this.credentials.clearPendingLogin()),
                tap(({ user, navigation }) => {
                    if (navigation === "preserve" && this.requestedQrCallback)
                        return;
                    const redirected =
                        navigation === "landing"
                            ? this.postLoginRedirect.consume(this.router.url)
                            : null;
                    const target =
                        redirected ??
                        (navigation === "landing" ||
                        this.isAnonymousEntryUrl(this.requestedUrl)
                            ? landingUrlFor(user)
                            : this.requestedUrl);
                    if (this.router.url !== target)
                        void this.router.navigateByUrl(target);
                })
            ),
        { dispatch: false }
    );

    readonly logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SessionActions.logoutRequested),
            tap(() => {
                const isAzureSession =
                    this.credentials.readTokens()?.source === "azure";
                this.authenticatedImages.clear();
                this.postLoginRedirect.clear();
                this.credentials.clearAll();
                if (isAzureSession)
                    window.location.assign(this.gateway.getAzureLogoutUrl());
            }),
            map(() => SessionActions.logoutCompleted())
        )
    );

    readonly logoutCompleted$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SessionActions.logoutCompleted),
                withLatestFrom(this.store.select(sessionFeature.selectStatus)),
                tap(() => void this.router.navigateByUrl("/login"))
            ),
        { dispatch: false }
    );

    private isAnonymousEntryUrl(url: string): boolean {
        return (
            url === "/" ||
            url.startsWith("/login") ||
            url.startsWith("/register") ||
            url.startsWith("/forgot-password") ||
            url.startsWith("/azure/") ||
            url.split(/[?#]/)[0] === "/azuread_accept_token"
        );
    }

    private errorMessage(error: unknown): string {
        return error instanceof Error
            ? error.message
            : this.translate.instant("auth.errors.loginFailed");
    }

    private isInvalidSession(error: unknown): boolean {
        return (
            error instanceof AuthenticationApiError &&
            (error.status === 401 || error.status === 403)
        );
    }

    private criticalLoadErrorMessage(error: unknown): string {
        const status =
            error instanceof AuthenticationApiError ||
            error instanceof HttpErrorResponse
                ? error.status
                : undefined;
        if (status === 0)
            return this.translate.instant("auth.workspaceLoad.network");
        if (status !== undefined && status >= 500)
            return this.translate.instant("auth.workspaceLoad.server");
        return this.translate.instant("auth.workspaceLoad.unknown");
    }
}
